<template>
	<AppPopup v-if="transaction"
		ref="refund-popup"
		:buttons="popupButtons"
		:title="getString('refund_transaction')"
		:z-index="zIndex"
		prevent-overlay-dismiss
		@toggling-popup="togglingPopup"
	>
		<div class="col-12 centered p-20 popup-refund" :class="{loading: loadingRefund}">
			<AppLoader v-show="loadingRefund" />
			<div class="row centered">
				<p v-html="getString('refund_x', [currencyStore.readableCurrencyValue(transaction.amount)])"></p>
				<AppButtonIcon class="ml-10" @click-icon="togglePartialAmount" small icon-name="icon-edit-white" />
			</div>
			<div class="row">
				<AppDropdown :collapsed="!showPartialAmount">
					<div class="row centered space-between p-10 amount-row">
						<p>{{ getString('amount') }}</p>
						<AppNumberInput double-decimal :symbol="currencyStore.currCurrency.symbol" :min="0" :max="currencyStore.dbToActualValue(transaction.amount)" :value="currencyStore.dbToActualValue(transaction.amount)" @changed-value="changedRefundValue" />
					</div>
				</AppDropdown>
			</div>
			<AppSelect
				class="mt-10"
				relative
				:label="getString('reason')"
				:values="[getString('reason_duplicate'), getString('reason_requested_by_customer')]"
				@selected="selectedReason"
			/>
		</div>
	</AppPopup>
</template>

<script>
import AppPopup from "@/shared/components/AppPopup.vue";
import AppLoader from "@/shared/components/AppLoader.vue";
import AppSelect from "@/shared/components/AppSelect.vue";
import AppDropdown from "@/shared/components/AppDropdown.vue";
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import AppNumberInput from "@/shared/components/AppNumberInput.vue";
import {useAdministrationStore} from "@/console-new/stores";
import {useCurrencyStore, useLanguageStore} from "@/shared/stores";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const currencyStore = useCurrencyStore();
		const administrationStore = useAdministrationStore();
		return {getString: languageStore.getString, currencyStore, administrationStore};
	},
	name: "RefundTransactionPopup",
	components: {AppPopup, AppLoader, AppSelect, AppDropdown, AppButtonIcon, AppNumberInput},
	emits: ["refunded", "failed", "closed"],
	props: {
		// normalized transaction (administrationStore.normalizeTransaction), null keeps the popup closed
		transaction: {
			type: Object,
			default: null,
		},
		zIndex: {
			type: Number,
			default: 1030,
		},
	},
	data() {
		return {
			loadingRefund: false,
			showPartialAmount: false,
			refundAmount: null,
			refundReason: null,
		}
	},
	computed: {
		popupButtons() {
			return this.loadingRefund ? [] : [
				{text: this.getString('cancel'), callback: () => {this.close()}, negative: true},
				{text: this.getString('ok'), callback: () => {this.refundTransaction()}},
			];
		},
	},
	methods: {
		togglePartialAmount() {
			this.showPartialAmount = !this.showPartialAmount;
		},
		changedRefundValue(value) {
			this.refundAmount = this.currencyStore.actualToDbValue(value);
		},
		selectedReason(index) {
			this.refundReason = index !== null ? ['duplicate', 'requested_by_customer'][index] : null;
		},
		close() {
			if(this.$refs['refund-popup']) {
				this.$refs['refund-popup'].toggleStatus(false);
			} else {
				this.$emit("closed");
			}
		},
		togglingPopup(isOpen) {
			if(!isOpen) {
				this.$emit("closed");
			}
		},
		refundTransaction() {
			if(this.loadingRefund) { return; }
			this.loadingRefund = true;
			let refundingAmount = this.refundingAmount();
			this.showPartialAmount = false;
			this.administrationStore.refundStripeTransaction(this.transaction.transaction_id, refundingAmount, this.refundReason || null, (success, details) => {
				this.loadingRefund = false;
				if(success) {
					this.$emit("refunded");
				} else {
					this.$emit("failed", details);
				}
				this.close();
			});
		},
		// null means full refund: the API treats a missing amount as the whole transaction,
		// so an untouched input or one left at the total refunds everything
		refundingAmount() {
			if(!this.showPartialAmount || this.refundAmount === null) {
				return null;
			}
			return this.refundAmount > 0 && this.refundAmount < this.transaction.amount ? this.refundAmount : null;
		},
	},
	watch: {
		transaction: {
			handler: function (transaction) {
				this.loadingRefund = false;
				this.showPartialAmount = false;
				this.refundAmount = null;
				this.refundReason = null;
				if(transaction) {
					this.$nextTick(() => {
						this.$refs['refund-popup'] && this.$refs['refund-popup'].toggleStatus(true);
					});
				}
			},
			immediate: true,
		},
	},
}
</script>
<style lang="scss" scoped>
.popup-refund {
	position: relative;

	&.loading > *:not(.loader-container) {
		opacity: 0;
	}

	&.loading .row .dropdown-container, &.loading .row .dropdown-container:deep(.dropdown) {
		opacity: 0;
	}

	.amount-row {
		gap: 20px;
	}

	.loader-container {
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
	}
}
</style>
